import { useKeyboardOverlap } from '@shared-ui/platform/hooks/use-keyboard-overlap'
import { useAppTheme, useCurrentColorPalette } from '@shared-ui/theme'
import { type CSSProperties, useMemo } from 'react'

const SHEET_OVERLAY_Z_INDEX = 1000
const SHEET_CONTENT_Z_INDEX = 1001
const SHEET_LAYER_Z_INDEX_STEP = 2
export type SheetDrawerStyles = {
  overlay: CSSProperties
  centeredHost: CSSProperties
  content: CSSProperties
  handleWrap: CSSProperties
  handle: CSSProperties
  body: CSSProperties
}

type UseSheetStylesOptions = {
  layerIndex?: number
  useBodyPortal?: boolean
  isFullscreen?: boolean
}

// Шиты (vaul) рендерят обычные DOM-элементы, поэтому стили — чистые CSSProperties,
// а не RN StyleSheet (createStyleSheet типизирован под RN ViewStyle).
export function useSheetStyles({
  layerIndex = 0,
  useBodyPortal = false,
  isFullscreen = false,
}: UseSheetStylesOptions = {}): SheetDrawerStyles {
  const theme = useAppTheme()
  const palette = useCurrentColorPalette()
  const keyboardInset = useKeyboardOverlap()
  const position = 'fixed'
  const overlayZIndex = SHEET_OVERLAY_Z_INDEX + layerIndex * SHEET_LAYER_Z_INDEX_STEP
  const contentZIndex = SHEET_CONTENT_Z_INDEX + layerIndex * SHEET_LAYER_Z_INDEX_STEP

  // TMA: 100dvh часто больше реального webview — берём --app-height (viewportStableHeight).
  const sheetMaxHeight =
    'min(calc(var(--app-height, 100dvh) * 0.92), calc(var(--app-height, 100dvh) - var(--safe-area-inset-top, 0px)))'
  const sheetWidth = useBodyPortal ? 'min(480px, calc(100vw - 48px))' : '100%'
  // При открытой клавиатуре ограничиваем высоту видимой областью над ней.
  const mobileMaxHeight =
    keyboardInset > 0 ? `min(${sheetMaxHeight}, calc(var(--app-height, 100dvh) - ${keyboardInset}px))` : sheetMaxHeight

  return useMemo(
    () => ({
      overlay: {
        position,
        inset: 0,
        zIndex: overlayZIndex,
        backgroundColor: theme.COLOR_BG_POPUP_OVERLAY,
      },
      centeredHost: {
        position,
        inset: 0,
        zIndex: contentZIndex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      },
      content: {
        ...(useBodyPortal
          ? {
              position: 'relative',
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column',
              width: sheetWidth,
              maxWidth: 480,
              maxHeight: sheetMaxHeight,
              backgroundColor: 'var(--color-bg-base)',
              color: 'var(--color-text-primary)',
              colorScheme: palette,
              borderRadius: theme.SIZE_SHEET_BORDER_RADIUS,
              overflow: 'hidden',
              boxShadow: '0 8px 40px rgba(8, 9, 28, 0.28)',
              outline: 'none',
            }
          : {
              position,
              left: 0,
              right: 0,
              // Поднимаем hug-content шторку над клавиатурой без раздувания высоты (vaul).
              bottom: keyboardInset,
              zIndex: contentZIndex,
              display: 'flex',
              flexDirection: 'column',
              width: sheetWidth,
              maxWidth: 480,
              marginLeft: 'auto',
              marginRight: 'auto',
              maxHeight: mobileMaxHeight,
              backgroundColor: 'var(--color-bg-base)',
              color: 'var(--color-text-primary)',
              colorScheme: palette,
              borderTopLeftRadius: theme.SIZE_SHEET_BORDER_RADIUS,
              borderTopRightRadius: theme.SIZE_SHEET_BORDER_RADIUS,
              overflow: 'hidden',
              boxShadow: '0 -8px 40px rgba(8, 9, 28, 0.28)',
              outline: 'none',
              // Home-indicator уже под клавиатурой — не дублируем отступ.
              paddingBottom: keyboardInset > 0 ? 0 : 'var(--safe-area-inset-bottom)',
            }),
        ...(isFullscreen
          ? {
              position,
              inset: 0,
              width: '100%',
              height: 'var(--app-height, 100dvh)',
              maxHeight: 'none',
              maxWidth: 'none',
              borderRadius: 0,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              paddingBottom: 0,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              zIndex: contentZIndex,
              backgroundColor: 'var(--color-bg-base)',
              color: 'var(--color-text-primary)',
              colorScheme: palette,
              overflow: 'hidden',
              outline: 'none',
              pointerEvents: 'auto',
            }
          : {}),
      },
      handleWrap: {
        display: useBodyPortal ? 'none' : 'flex',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        justifyContent: 'center',
        paddingTop: 6,
        paddingBottom: 4,
        cursor: 'grab',
      },
      handle: {
        width: 40,
        height: 4,
        borderRadius: 999,
        backgroundColor: theme.COLOR_BG_DISABLED,
      },
      // Как ServiceBottomSheet: высокий контент (Закрыть карту) должен скроллиться,
      // иначе в TMA низ кнопки обрезается overflow:hidden.
      body: {
        ...(useBodyPortal
          ? {
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
              overflowY: 'auto',
              maxHeight: sheetMaxHeight,
              paddingBottom: 0,
            }
          : {
              flex: 1,
              minHeight: 0,
              overflowX: 'hidden',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 0,
              maxHeight: mobileMaxHeight,
            }),
        ...(isFullscreen
          ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              maxHeight: 'none',
              paddingBottom: 0,
            }
          : {}),
      },
    }),
    [
      contentZIndex,
      keyboardInset,
      isFullscreen,
      mobileMaxHeight,
      overlayZIndex,
      position,
      sheetMaxHeight,
      sheetWidth,
      theme,
      palette,
      useBodyPortal,
    ],
  )
}
